import * as React from 'react';
import type {
  StreamingServicePhase,
  WorkspaceSim,
  WorkspaceSimsSnapshot,
  WorkspaceStatus,
  WorkspaceStreamTarget,
} from '@/lib/types/workspace';
import { isLocalTarget } from '@/lib/utils/workspace';
import { WORKSPACE_STATUS_REFRESH_EVENT } from '@/lib/workspaceStatus/events';
import { useServiceTokens } from './ServiceTokensProvider';
import { useWorkspace } from './WorkspaceProvider';
import { getLegacySimStatus, legacyCompatSnapshot } from './workspaceStatus.legacy';

type ServiceStatus = 'ready' | 'unreachable' | 'unknown' | 'unavailable';
type SimServiceStatus = 'absent' | 'starting' | 'ready' | 'stopped' | 'unreachable';
type StatusEndpointAvailability = 'available' | 'missing' | 'transient';

/**
 * Compact control-plane snapshot from the workspace server.
 *
 * The workspace server is the control plane: workspace-server / Jupyter
 * / Rerun health plus the live sims registry. It no longer carries the
 * sim's own status — the browser reads that from the sim's
 * `/sim/status/stream` directly (see SimStatusProvider).
 *
 * `sim` / `streaming` are LEGACY: workspace pods predating
 * `/sim/status/stream` nest the sim's full payload here, and
 * SimStatusProvider's legacy reader uses it as a fallback. Modern pods
 * send a registry-liveness stub (`status_payload: null`) and omit
 * `streaming`. See {@link ./simStatus.legacy}.
 */
export interface WorkspaceBackendSnapshot {
  version: number;
  seq: number;
  server_ts_ms: number;
  workspace_server: { status: ServiceStatus; port?: number };
  auth?: { status: string; seed_status?: string; last_success_ts_ms?: number | null; last_error?: string | null };
  jupyter: { status: ServiceStatus; port?: number };
  sim?: {
    status: SimServiceStatus;
    reachable: boolean;
    status_payload: WorkspaceStatus | null;
  };
  streaming?: { status: StreamingServicePhase; port?: number };
  rerun: { status: ServiceStatus; port?: number };
  sims?: WorkspaceSimsSnapshot;
}

export interface WorkspaceStatusContextValue {
  /** Raw control-plane snapshot. Exposed so SimStatusProvider's legacy reader can see old pods' nested sim payload. */
  snapshot: WorkspaceBackendSnapshot | null;
  rerunReady: boolean;
  sims: WorkspaceSim[];
  livestreamHolderPid: number | null;
  killSim: (sessionId: string) => Promise<KillSimResult>;
  refresh: (reason: string) => void;
}

export type KillSimResult = { status: 'killed' | 'kill_failed' | 'not_found'; pid?: number; session_id?: string };

const WorkspaceStatusContext = React.createContext<WorkspaceStatusContextValue | null>(null);

const WS_RECONNECT_INITIAL_MS = 1000;
const WS_RECONNECT_MAX_MS = 10_000;
const COMPAT_STARTING_INTERVAL_MS = 1000;
const COMPAT_HEALTHY_INTERVAL_MS = 2500;
const COMPAT_IDLE_INTERVAL_MS = 5000;

// Compatibility for workspaces started from images that predate the
// consolidated workspace-server status stream. Current images expose
// /workspace/status/stream; the compat-agent endpoints cover one
// generation older.
type StatusEndpointKind = 'workspace' | 'compat-agent';

interface StatusEndpoint {
  kind: StatusEndpointKind;
  httpUrl: string;
  wsUrl: string;
}

export function statusStreamPreOpenCloseAction(
  endpointKind: StatusEndpointKind,
  endpointAvailable: StatusEndpointAvailability
): 'reconnect' | 'try_compat' | 'legacy_fallback' {
  if (endpointAvailable !== 'available') return 'reconnect';
  return endpointKind === 'workspace' ? 'try_compat' : 'legacy_fallback';
}

function workspaceServerHttpUrl(
  target: WorkspaceStreamTarget,
  workspaceToken: string | undefined,
  kind: StatusEndpointKind
): string {
  const path = kind === 'workspace' ? '/workspace/status' : '/agent/workspace/status';
  if (isLocalTarget(target)) return path;
  const url = new URL(path, target.httpBaseUrl);
  if (workspaceToken) url.searchParams.set('token', workspaceToken);
  return url.toString();
}

function workspaceServerWsUrl(
  target: WorkspaceStreamTarget,
  workspaceToken: string | undefined,
  kind: StatusEndpointKind
): string {
  const path = kind === 'workspace' ? '/workspace/status/stream' : '/agent/workspace/status/stream';
  const base = isLocalTarget(target)
    ? typeof window !== 'undefined'
      ? window.location.origin
      : 'http://localhost'
    : target.httpBaseUrl;
  const url = new URL(path, base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  if (workspaceToken) url.searchParams.set('token', workspaceToken);
  return url.toString();
}

function statusEndpoint(
  target: WorkspaceStreamTarget,
  workspaceToken: string | undefined,
  kind: StatusEndpointKind
): StatusEndpoint {
  return {
    kind,
    httpUrl: workspaceServerHttpUrl(target, workspaceToken, kind),
    wsUrl: workspaceServerWsUrl(target, workspaceToken, kind),
  };
}

function killSimUrl(target: WorkspaceStreamTarget, workspaceToken: string | undefined, sessionId: string): string {
  const path = `/workspace/sims/${encodeURIComponent(sessionId)}/kill`;
  if (isLocalTarget(target)) return path;
  const url = new URL(path, target.httpBaseUrl);
  if (workspaceToken) url.searchParams.set('token', workspaceToken);
  return url.toString();
}

function validateSnapshot(value: unknown): WorkspaceBackendSnapshot | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as WorkspaceBackendSnapshot & { agent?: WorkspaceBackendSnapshot['workspace_server'] };
  const snapshot = {
    ...raw,
    workspace_server: raw.workspace_server ?? raw.agent ?? { status: 'unknown' },
  } as WorkspaceBackendSnapshot;
  if (!snapshot.workspace_server || typeof snapshot.workspace_server !== 'object') return null;
  return snapshot;
}

export function shouldRunWorkspaceStatusStream({
  hasStreamTarget,
  tokensReady,
}: {
  hasStreamTarget: boolean;
  tokensReady: boolean;
}): boolean {
  return hasStreamTarget && tokensReady;
}

export function WorkspaceStatusProvider({ children }: { children: React.ReactNode }) {
  const { streamTarget } = useWorkspace();
  const { tokens: serviceTokens, isLoading: tokensLoading } = useServiceTokens();
  const [snapshot, setSnapshot] = React.useState<WorkspaceBackendSnapshot | null>(null);
  const [compatFallbackActive, setCompatFallbackActive] = React.useState(false);
  const [refreshNonce, refresh] = React.useReducer((n: number) => n + 1, 0);
  const requestRefresh = React.useCallback((_reason: string) => refresh(), []);

  const isLocal = streamTarget ? isLocalTarget(streamTarget) : false;
  const workspaceToken = isLocal ? undefined : (serviceTokens?.workspace_token ?? undefined);
  const tokensReady = isLocal || (!tokensLoading && serviceTokens !== null);
  const enabled = shouldRunWorkspaceStatusStream({ hasStreamTarget: streamTarget !== null, tokensReady });
  const workspaceEndpoint = React.useMemo(
    () => (streamTarget ? statusEndpoint(streamTarget, workspaceToken, 'workspace') : null),
    [streamTarget, workspaceToken]
  );
  const compatEndpoint = React.useMemo(
    () => (streamTarget ? statusEndpoint(streamTarget, workspaceToken, 'compat-agent') : null),
    [streamTarget, workspaceToken]
  );
  const targetUrl = streamTarget?.httpBaseUrl ?? null;

  React.useEffect(() => {
    const onRefresh = () => refresh();
    window.addEventListener(WORKSPACE_STATUS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(WORKSPACE_STATUS_REFRESH_EVENT, onRefresh);
  }, []);

  React.useEffect(() => {
    void targetUrl;
    setSnapshot(null);
    setCompatFallbackActive(false);
  }, [targetUrl]);

  // Control-plane status stream. Current pods expose /workspace/status/stream;
  // one generation older used the compat-agent route. If both are missing,
  // fall back to direct /sim/status polling so already-running older
  // workspaces remain observable until they are restarted onto a newer image.
  React.useEffect(() => {
    if (!enabled || !workspaceEndpoint || !compatEndpoint) {
      setCompatFallbackActive(false);
      return;
    }

    const primary = workspaceEndpoint;
    const compat = compatEndpoint;
    let cancelled = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = WS_RECONNECT_INITIAL_MS;

    const scheduleReconnect = (endpoint: StatusEndpoint) => {
      reconnectTimer = setTimeout(() => void connect(endpoint), reconnectDelay);
      reconnectDelay = Math.min(Math.round(reconnectDelay * 1.6), WS_RECONNECT_MAX_MS);
    };

    async function fetchInitialSnapshot(endpoint: StatusEndpoint): Promise<StatusEndpointAvailability> {
      try {
        const response = await fetch(endpoint.httpUrl, {
          method: 'GET',
          credentials: isLocal ? 'same-origin' : 'include',
          signal: AbortSignal.timeout(2500),
        });
        if (response.status === 404 || (response.status >= 400 && response.status < 500)) return 'missing';
        if (!response.ok) throw new Error(`workspace status ${response.status}`);
        const next = validateSnapshot(await response.json());
        if (!next) return 'missing';
        if (!cancelled) setSnapshot(next);
        return 'available';
      } catch {
        return 'transient';
      }
    }

    async function connect(endpoint: StatusEndpoint = primary) {
      setCompatFallbackActive(false);
      const endpointAvailable = await fetchInitialSnapshot(endpoint);
      if (cancelled) return;
      if (endpointAvailable === 'missing' && endpoint.kind === 'workspace') {
        void connect(compat);
        return;
      }
      if (endpointAvailable === 'missing') {
        setCompatFallbackActive(true);
        return;
      }

      socket = new WebSocket(endpoint.wsUrl);
      let opened = false;
      socket.onopen = () => {
        opened = true;
        reconnectDelay = WS_RECONNECT_INITIAL_MS;
      };
      socket.onmessage = (event) => {
        // A message already queued on the old socket can dispatch after this
        // effect's cleanup (workspace switch). Drop it so the new target can't
        // briefly inherit the previous workspace's sims / Rerun state.
        if (cancelled) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(String(event.data));
        } catch {
          return;
        }
        const next = validateSnapshot(parsed);
        if (next) setSnapshot(next);
      };
      socket.onerror = () => {
        socket?.close();
      };
      socket.onclose = () => {
        if (cancelled) return;
        if (!opened) {
          switch (statusStreamPreOpenCloseAction(endpoint.kind, endpointAvailable)) {
            case 'try_compat':
              void connect(compat);
              return;
            case 'legacy_fallback':
              setCompatFallbackActive(true);
              return;
            case 'reconnect':
              scheduleReconnect(endpoint);
              return;
          }
        }
        scheduleReconnect(primary);
      };
    }

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [enabled, workspaceEndpoint, compatEndpoint, isLocal]);

  // Eager one-shot refresh of the control-plane snapshot (e.g. right
  // after killSim) so the UI reflects a registry change without waiting
  // for the next stream tick. Control-plane GET only — never /sim/status.
  React.useEffect(() => {
    void refreshNonce;
    if (!enabled || !workspaceEndpoint || !compatEndpoint || compatFallbackActive) return;

    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        let response = await fetch(workspaceEndpoint.httpUrl, {
          method: 'GET',
          credentials: isLocal ? 'same-origin' : 'include',
          signal: AbortSignal.timeout(2500),
        });
        if (response.status === 404) {
          response = await fetch(compatEndpoint.httpUrl, {
            method: 'GET',
            credentials: isLocal ? 'same-origin' : 'include',
            signal: AbortSignal.timeout(2500),
          });
        }
        if (!response.ok) return;
        const next = validateSnapshot(await response.json());
        if (!cancelled && next) setSnapshot(next);
      } catch {
        // The stream is the source of truth; ignore one-shot failures.
      }
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [refreshNonce, enabled, workspaceEndpoint, compatEndpoint, isLocal, compatFallbackActive]);

  React.useEffect(() => {
    void refreshNonce;
    if (!enabled || !streamTarget || !compatFallbackActive) return;

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      const { status, failureMode } = await getLegacySimStatus(streamTarget.httpBaseUrl, workspaceToken, true);
      if (cancelled) return;
      if (status === null && failureMode !== 'http_503') {
        timeout = setTimeout(poll, COMPAT_IDLE_INTERVAL_MS);
        return;
      }
      const reachable = status !== null;
      setSnapshot(legacyCompatSnapshot(status, reachable));

      const interval =
        status?.state === 'starting'
          ? COMPAT_STARTING_INTERVAL_MS
          : status?.healthy
            ? COMPAT_HEALTHY_INTERVAL_MS
            : COMPAT_IDLE_INTERVAL_MS;
      timeout = setTimeout(poll, interval);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [enabled, streamTarget, workspaceToken, compatFallbackActive, refreshNonce]);

  const killSim = React.useCallback(
    async (sessionId: string): Promise<KillSimResult> => {
      if (!streamTarget) return { status: 'not_found' };
      const url = killSimUrl(streamTarget, workspaceToken, sessionId);
      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: isLocal ? 'same-origin' : 'include',
          signal: AbortSignal.timeout(15_000),
        });
        if (response.status === 404) {
          refresh();
          return { status: 'not_found', session_id: sessionId };
        }
        if (!response.ok) return { status: 'kill_failed' };
        const payload = (await response.json()) as KillSimResult;
        // Eagerly nudge the snapshot stream — the next status tick will
        // confirm the entry has been swept.
        refresh();
        return payload;
      } catch {
        return { status: 'kill_failed' };
      }
    },
    [streamTarget, workspaceToken, isLocal]
  );

  const value = React.useMemo<WorkspaceStatusContextValue>(() => {
    const sims = snapshot?.sims?.sims ?? [];
    return {
      snapshot,
      rerunReady: snapshot?.rerun?.status === 'ready',
      sims,
      livestreamHolderPid: snapshot?.sims?.livestream_holder_pid ?? null,
      killSim,
      refresh: requestRefresh,
    };
  }, [snapshot, killSim, requestRefresh]);

  return <WorkspaceStatusContext.Provider value={value}>{children}</WorkspaceStatusContext.Provider>;
}

export function useWorkspaceStatus(): WorkspaceStatusContextValue {
  const value = React.useContext(WorkspaceStatusContext);
  if (!value) throw new Error('useWorkspaceStatus must be used within WorkspaceStatusProvider');
  return value;
}
